import { useMutation, useQueryClient } from "@tanstack/react-query";

import { actionKey, apiPost, qk, resetActionKey } from "@/shared/api";

/**
 * Покупка робота.
 *
 * Ключ идемпотентности рождается на СТАРТ ДЕЙСТВИЯ — то есть привязан к
 * роботу, а не к попытке отправки. Если бы он создавался внутри `mutationFn`,
 * повтор после таймаута ушёл бы с новым ключом, и бэкенд честно провёл бы
 * вторую покупку.
 *
 * Ключ сбрасывается только на успехе. При бизнес-ошибке (например, не хватило
 * денег) бэкенд откатывает резервацию сам, поэтому повтор тем же ключом после
 * пополнения корректен.
 *
 * Сумма входит в ключ (ТЗ §16). Бэкенд считает отпечаток вместе с телом, и тот
 * же ключ с другой суммой он отвергает 409 — то есть пользователь, поправивший
 * сумму после отказа, получил бы конфликт вместо новой попытки.
 */
export interface BuyPayload {
  amount: string;
  acceptedRisks: boolean;
}

export function useBuyRobot(robotId: number) {
  const client = useQueryClient();
  const keyFor = (amount: string) => `buy:${String(robotId)}:${amount}`;

  return useMutation({
    mutationFn: ({ amount, acceptedRisks }: BuyPayload) =>
      apiPost("/api/v1/robots/{robot_id}/buy", {
        params: { robot_id: robotId },
        body: { amount, accepted_risks: acceptedRisks },
        idempotencyKey: actionKey(keyFor(amount)),
      }),
    onSuccess: (_result, { amount }) => {
      resetActionKey(keyFor(amount));
      // Покупка двигает баланс, портфель, историю операций и ленту событий.
      // `["home"]` без периода — префикс: ключ главной параметризован
      // периодом графика, и точное совпадение сбросило бы только один из них.
      //
      // Каталог и карточка робота — тоже: у лимитированного робота покупка
      // сдвигает шкалу заполненности, и §9.1.4 требует, чтобы она обновилась
      // сразу. `["robots"]` префиксом, потому что ключ каталога параметризован
      // фильтром рынка.
      for (const key of [
        ["home"],
        ["robots"],
        qk.robot(robotId),
        qk.wallet(),
        qk.myRobots(),
        qk.profile(),
      ]) {
        void client.invalidateQueries({ queryKey: key });
      }
      void client.invalidateQueries({ queryKey: ["wallet", "transactions"] });
      void client.invalidateQueries({ queryKey: ["activity"] });
    },
  });
}
